/**
 * Core-100 CORE-AY — one search for tickets, customers, vehicles and employees.
 *
 * Sources (both server-side, both under the caller's own authority):
 *   - `universal_search` RPC (security invoker): tickets by displayed number / customer / vehicle / id across
 *     ALL dates, and employees by name;
 *   - `customer-search` Edge function: customers and vehicles by name, phone, email, VIN or plate through the
 *     blind index; protected values come back masked, never in full.
 *
 * The constitution: accept whichever identifier the searcher has; a result leads to the thing itself; be
 * honest about scope — a source that failed is reported as failed, never as "nothing found".
 */
import type { CustomerSearchResult } from '@/lib/customers/types';
import { CUSTOMER_SEARCH_FAILED, CUSTOMER_SEARCH_RATE_LIMITED, CUSTOMER_SEARCH_SCOPE } from './customerSearchContract';

export interface UniversalSearchRow {
  kind: 'ticket' | 'employee';
  entity_id: string;
  title: string;
  subtitle: string;
  status: string;
  day_label: string | null;
  rank: number;
  /** R4: for an employee, the Passport (technician_profile_id) the result opens; null when there is none. */
  link_id?: string | null;
}

export interface UniversalResult {
  key: string;
  group: 'Tickets' | 'Customers' | 'Vehicles' | 'Employees';
  title: string;
  subtitle: string;
  status?: string;
  /**
   * Where the result leads: a route, plus the Dayboard day for tickets. `path` is null only when there is no
   * surface this viewer can open for the thing found — and then `note` says so on the result itself, so a found
   * thing never silently goes nowhere (CORE-AY: "a search that finds something and then abandons the user has
   * not finished").
   */
  target: { path: string | null; dayLabel?: string; note?: string };
}

export interface UniversalMergeOptions {
  /** The viewer may open the roster editor (`/technicians/:id/edit`: owner, manager, service writer). */
  canOpenRoster?: boolean;
  /**
   * R4 CORE-AY: why customers/vehicles were not searched, when `customers` is null (e.g. the search bound's
   * CUSTOMER_SEARCH_RATE_LIMITED). Defaults to CUSTOMER_SEARCH_FAILED.
   */
  customerFailure?: string | null;
  /** R4 CORE-AY: the customer search was refused by the server's search bound (429). */
  customerRateLimited?: boolean;
  /** R5 CORE-AY: universal_search (tickets, employees) was refused by the same per-user bound (PT429). */
  ticketsRateLimited?: boolean;
}

/**
 * R4 — an employee result leads to the PERSON: their Passport when the roster row has one, else (for a viewer who
 * may open the roster) that person's roster record. Never the generic roster list.
 */
export function employeeTarget(r: Pick<UniversalSearchRow, 'entity_id' | 'link_id'>, opts: UniversalMergeOptions = {}): UniversalResult['target'] {
  if (r.link_id) return { path: `/technicians/${encodeURIComponent(r.link_id)}` };
  if (opts.canOpenRoster) return { path: `/technicians/${encodeURIComponent(r.entity_id)}/edit` };
  return { path: null, note: 'No Passport to open for this person.' };
}

export interface UniversalSearchOutcome {
  results: UniversalResult[];
  /** What was searched and what could not be — shown with the results. */
  scope: string[];
  /**
   * R4 CORE-AY: set when the server refused the customer search because this person searched too often. Shown in
   * place of "nothing found" — a refused search is never presented as an empty one.
   */
  refusal?: string | null;
}

export function mergeUniversalResults(
  rows: UniversalSearchRow[] | null,
  customers: CustomerSearchResult[] | null,
  opts: UniversalMergeOptions = {},
): UniversalSearchOutcome {
  const results: UniversalResult[] = [];
  const scope: string[] = [];

  let refusal: string | null = null;
  if (rows === null) {
    // R5 CORE-AY: a refusal by the search bound is said as such, never as "nothing found" or a generic failure.
    if (opts.ticketsRateLimited) {
      scope.push(`Tickets and employees were not searched: ${CUSTOMER_SEARCH_RATE_LIMITED}`);
      refusal = CUSTOMER_SEARCH_RATE_LIMITED;
    } else {
      scope.push('Tickets and employees could not be searched just now.');
    }
  } else {
    // Core-100 R4 W4 (CORE-AY): the server matches 2 characters from the start of a name only, and 3+ anywhere; the
    // typed text is literal (no wildcards). Say so, so an empty result for a 2-character fragment is not "absent".
    scope.push('Tickets of every date (by number, customer or vehicle) and employees by name: two characters match the start of a name, three or more match anywhere.');
    for (const r of [...rows].sort((a, b) => a.rank - b.rank)) {
      if (r.kind === 'ticket') {
        results.push({
          key: `t:${r.entity_id}`, group: 'Tickets', title: r.title, subtitle: r.subtitle, status: r.status,
          target: { path: `/?tab=dayboard&highlight=${encodeURIComponent(r.entity_id)}`, dayLabel: r.day_label ?? undefined },
        });
      } else {
        results.push({
          key: `e:${r.entity_id}`, group: 'Employees', title: r.title, subtitle: r.subtitle, status: r.status,
          target: employeeTarget(r, opts),
        });
      }
    }
  }

  if (customers === null) {
    scope.push(opts.customerFailure || CUSTOMER_SEARCH_FAILED);
    if (opts.customerRateLimited) refusal = CUSTOMER_SEARCH_RATE_LIMITED;
  } else {
    // R4: honest about how much of a protected number is enough — a VIN by its last 8, a plate by its ending (3+).
    scope.push(CUSTOMER_SEARCH_SCOPE);
    const seenCustomers = new Set<string>();
    for (const c of customers) {
      const isVehicleMatch = c.match_type === 'vin' || c.match_type === 'plate';
      const vehicle = [c.vehicle_year, c.vehicle_make, c.vehicle_model].filter(Boolean).join(' ');
      if (isVehicleMatch && c.vehicle_id) {
        results.push({
          key: `v:${c.vehicle_id}`, group: 'Vehicles', title: vehicle || 'Vehicle',
          subtitle: [c.customer_name, c.vin_masked && `VIN ${c.vin_masked}`, c.plate_masked && `Plate ${c.plate_masked}`].filter(Boolean).join(' · '),
          target: { path: c.customer_id ? `/customers/${encodeURIComponent(c.customer_id)}` : '/customers' },
        });
      } else if (c.customer_id && !seenCustomers.has(c.customer_id)) {
        seenCustomers.add(c.customer_id);
        results.push({
          key: `c:${c.customer_id}`, group: 'Customers', title: c.customer_name || c.display_label,
          subtitle: [c.phone_masked, c.email_masked, c.vehicle_count ? `${c.vehicle_count} vehicle${c.vehicle_count === 1 ? '' : 's'}` : null].filter(Boolean).join(' · '),
          target: { path: `/customers/${encodeURIComponent(c.customer_id)}` },
        });
      }
    }
  }
  return { results, scope, refusal };
}

export const UNIVERSAL_GROUP_ORDER: UniversalResult['group'][] = ['Tickets', 'Customers', 'Vehicles', 'Employees'];
